import { isDefined } from 'class-validator';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { ReactNode, useEffect } from 'react';

import getTikTokPixelScript from '@pflegia/analytics/ui/scripts/get-tiktok-pixel-script';
import { getFeatureFlagsProperties } from '@pflegia/feature-flags/utils/helpers/get-feature-flag-properties';
import { Flavor } from '@pflegia/types/common/enums/flavor.enum';
import { CandidateActions } from '@pflegia/types/common/enums/post-back/candidate-actions.enum';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useCookiesConsentManagerContext } from '@pflegia/ui/contexts/cookies-consent-manager.context';
import { useThrottleToast } from '@pflegia/ui/shared/hooks/use-throttle-toast';

import useHandlePausedAccount from '@hooks/use-handle-paused-account';

import { segmentService } from '@shared/application/analytics/segment.service';
import {
  CANDIDATE_SECOND_STEP_REGISTRATION,
  CHAT_CONVERSATION,
  CHAT_ROOT,
  EXTERNAL_JOB,
  FAST_TRACK,
  FILE,
  FOR_EMPLOYERS,
  FUNNEL,
  HOME,
  IMPRINT_AND_PRIVACY,
  INTERESTED_JOB_LINK,
  MAGIC_LINK,
  MAINTENANCE,
  PAGES_WITH_CUSTOM_TRACKING,
  SET_PASSWORD,
  SLIMMED_DOWN_FUNNEL,
  TERMS_AND_CONDITIONS,
  UNSUBSCRIBE,
} from '@shared/application/constants';
import { useHideJobBoardLink } from '@shared/hooks/use-hide-job-board-link';
import { useTrackUtm } from '@shared/hooks/use-track-utm';
import { accessTokenService } from '@shared/utils/access-token.service';

import { useGetNotifications } from '../application/use-get-notifications';
import { useVerifyPasswordNotSet } from '../hooks/use-verify-password-not-set';
import FooterSkeleton from './footer/FooterSkeleton';
import HeaderSkeleton from './header/HeaderSkeleton';
import OrganizationJsonLdBase from './organization-json-ld-base/OrganizationJsonLdBase';
import SegmentConsentManager from './segment/SegmentConsentManager';

const Header = dynamic(() => import('./header'), {
  loading: () => <HeaderSkeleton />,
  ssr: false, // auth-dependent content causes hydration mismatches
});
const Footer = dynamic(() => import('./footer/Footer.component'), {
  loading: () => <FooterSkeleton />,
  ssr: false,
});

const PixelComponent = dynamic(() => import('@pflegia/ui/shared/PixelComponent'), { ssr: false });
const TawkComponent = dynamic(() => import('@pflegia/ui/shared/TawkComponent'), { ssr: false });
const pagesWithoutConsent = [IMPRINT_AND_PRIVACY, TERMS_AND_CONDITIONS];
const pagesWithoutTawk = [FOR_EMPLOYERS, FUNNEL, SLIMMED_DOWN_FUNNEL, CANDIDATE_SECOND_STEP_REGISTRATION];

const routeChangeCompleteHandler = (url: string) => {
  segmentService.page(url, {
    ...getFeatureFlagsProperties(),
  });
  accessTokenService.identifyUser();
};

interface Props {
  children: ReactNode;
}

const Page: React.FC<Props> = ({ children }) => {
  const { ready, cookiesSettings } = useCookiesConsentManagerContext();
  const { pathname, events, route, query, isReady } = useRouter();
  const authenticated = useAuth();
  const { trackUtm } = useTrackUtm();

  useHandlePausedAccount();
  useVerifyPasswordNotSet();
  useGetNotifications();

  useThrottleToast();

  useEffect(() => {
    if (
      ((isDefined(pathname) && pathname === HOME) || ![FUNNEL, CANDIDATE_SECOND_STEP_REGISTRATION, MAGIC_LINK].includes(pathname)) &&
      localStorage.getItem(INTERESTED_JOB_LINK)
    )
      localStorage.removeItem(INTERESTED_JOB_LINK);
  }, [pathname]);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const isCustomTrackedPage = PAGES_WITH_CUSTOM_TRACKING.some(page => url.startsWith(page));

      if (!isCustomTrackedPage) routeChangeCompleteHandler(url);
      trackUtm(CandidateActions.Arrived);
    };

    events.on('routeChangeComplete', handleRouteChange);
    return () => events.off('routeChangeComplete', handleRouteChange);
  }, [events, trackUtm]);

  const hideHeaderAndFooterOnMobile = pathname.endsWith(CHAT_CONVERSATION) && Boolean(query.slugs);
  const isFile = pathname === FILE;
  const isInvalidFlavor = isReady && isDefined(query.flavor) && ![Flavor.Pflegia, Flavor.Praxia].includes(query.flavor as Flavor);
  const showHeaderAndFooter =
    !isInvalidFlavor &&
    ![FUNNEL, FAST_TRACK, CANDIDATE_SECOND_STEP_REGISTRATION, MAINTENANCE, SET_PASSWORD].some(page => route.includes(page));
  const isExternal = pathname.includes(EXTERNAL_JOB);
  const hideFooter = [CHAT_ROOT, UNSUBSCRIBE].some(page => pathname.includes(page));

  const hideJobBoard = useHideJobBoardLink();

  const shouldShowConsentModal = !pagesWithoutConsent.some(page => pathname.includes(page));
  const shouldHideTawkComponent = pagesWithoutTawk.includes(pathname) || !authenticated;

  const consentManager = process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY && (
    <SegmentConsentManager key='segment-consent-manager' hideConsentModal={isFile || !shouldShowConsentModal} />
  );

  // If we use our proxy page to view files we do not show the layout
  if (isFile)
    return (
      <>
        {children}
        {consentManager}
      </>
    );

  return (
    <>
      <OrganizationJsonLdBase />
      <div className='flex min-h-svh flex-col'>
        {showHeaderAndFooter && !isExternal && (
          <div className={clsx(hideHeaderAndFooterOnMobile && 'hidden md:block')}>
            <Header />
          </div>
        )}
        <div className='flex grow flex-col'>{children}</div>
        <div className={clsx('z-10', !(showHeaderAndFooter && !hideFooter) && 'hidden', hideHeaderAndFooterOnMobile && 'hidden md:block')}>
          <Footer showJobBoard={!hideJobBoard} isLoggedIn={authenticated} />
        </div>
      </div>
      {consentManager}
      {ready && cookiesSettings.advertising && (
        <PixelComponent id='tiktokPixel' script={getTikTokPixelScript(process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID)} />
      )}
      <TawkComponent hideOnPages={shouldHideTawkComponent} />
    </>
  );
};

export default Page;
